import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import { classNames } from "@/helpers/classNames";

import MediaWrapper from "@/components/mediaWrapper";
import LinkWrapper from "@/components/linkWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";
import FooterCopyrightText from "@/components/layout-blocks/footer/footer-copyright-text";

export const query = graphql`
  fragment FooterColumnsWithLeadingCompanyLogoArticle on StrapiArticle {
    locale
    date
    url
    title
  }

  fragment FooterColumnsWithLeadingCompanyLogoProcessedProduct on ShopifyProcessedProduct {
    title
    url
    productType
    productTypeUrl
  }

  fragment FooterColumnsWithLeadingCompanyLogoData on StrapiComponentFooterColumnsWithLeadingCompanyLogo {
    companyLogo {
      ...StrapiSharedLink
    }
    socialLinks {
      ...StrapiSharedLink
    }
    colOneTitle
    colOneLinks {
      ...StrapiSharedLink
    }
    colTwoTitle
    colTwoLinks {
      ...StrapiSharedLink
    }
    blogTitle
    addressTitle
    address
    certificate {
      ...StrapiSharedLink
    }
    copyrightLink {
      ...StrapiSharedLink
    }
  }
`;

function createLinkColumn(title, links) {
  return (
    <div>
      {title && (
        <h3 className="text-sm font-semibold uppercase tracking-wider text-text-on-primary-light">
          <MarkdownWrapper text={title} />
        </h3>
      )}
      <ul role="list" className="mt-4 space-y-4">
        {Boolean(links?.length) &&
          links.map(
            (item, index) =>
              (item.title || item.overwriteTitle || item.link?.title) && (
                <li key={index}>
                  <LinkWrapper
                    to={item.url || item.link?.url}
                    className="text-base text-text-on-primary hover:text-text-on-primary-light"
                  >
                    <MarkdownWrapper
                      text={
                        item.title || item.overwriteTitle || item.link?.title
                      }
                    />
                  </LinkWrapper>
                </li>
              )
          )}
      </ul>
    </div>
  );
}

function ColumnsWithLeadingCompanyLogo({ data, locale, className }) {
  if (!data || !locale) {
    return <></>;
  }

  const query = useStaticQuery(graphql`
    query colmunsWithLeadingCompanyLogo {
      articles: allStrapiArticle(sort: { date: DESC }) {
        nodes {
          ...FooterColumnsWithLeadingCompanyLogoArticle
        }
      }
    }
  `);
  const articles =
    query?.articles?.nodes?.filter((l) => l.locale === locale).slice(0, 3) ||
    [];

  return (
    <footer
      className={classNames("bg-background-primary", className)}
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-x-8 gap-y-16 xs:grid-cols-2 sm:grid-cols-3 xl:grid-cols-5">
          <div className="grid grid-cols-1 content-between">
            <div className="flex justify-start">
              {(data.companyLogo?.overwriteIcon ||
                data.companyLogo?.link?.icon) && (
                <LinkWrapper to={data.companyLogo?.link?.url}>
                  <MediaWrapper
                    media={
                      data.companyLogo?.overwriteIcon ||
                      data.companyLogo.link.icon
                    }
                    objectFit="contain"
                    divClassName="aspect-[1/1] h-20"
                  />
                </LinkWrapper>
              )}
            </div>
            {Boolean(data.socialLinks?.length) && (
              <ul className="mt-4 flex space-x-6">
                {data.socialLinks.map(
                  (socialLink, index) =>
                    (socialLink.overwriteIcon || socialLink.link?.icon) && (
                      <li key={index}>
                        <LinkWrapper
                          to={socialLink.link?.url}
                          className="brightness-[1] hover:brightness-[3]"
                        >
                          <MediaWrapper
                            media={
                              socialLink.overwriteIcon || socialLink.link.icon
                            }
                            objectFit="contain"
                            divClassName="w-5 h-5"
                          />
                        </LinkWrapper>
                      </li>
                    )
                )}
              </ul>
            )}
          </div>
          <div>{createLinkColumn(data.colOneTitle, data.colOneLinks)}</div>
          <div>{createLinkColumn(data.colTwoTitle, data.colTwoLinks)}</div>
          <div>{createLinkColumn(data.blogTitle, articles)}</div>
          <div className="">
            {data.addressTitle && (
              <h3 className="text-sm font-semibold uppercase tracking-wider text-text-on-primary-light">
                <MarkdownWrapper text={data.addressTitle} />
              </h3>
            )}
            {data.address && (
              <MarkdownWrapper
                text={data.address}
                markdownClassName="text-text-on-primary mt-4 text-base"
                linkClassName="underline font-bold hover:text-text-on-primary-light"
                replaceParagraph={false}
              />
            )}
            {(data.certificate?.overwriteIcon ||
              data.certificate?.link?.icon) && (
              <div className="mt-4 flex justify-start">
                <LinkWrapper to={data.certificate?.link?.url}>
                  <MediaWrapper
                    media={
                      data.certificate?.overwriteIcon ||
                      data.certificate.link.icon
                    }
                    objectFit="contain"
                    objectPosition="0% 0%"
                    divClassName="h-20"
                  />
                </LinkWrapper>
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 flex justify-center border-t border-border-on-primary pt-8">
          <FooterCopyrightText
            className="text-text-on-primary"
            linkClassName="hover:text-text-on-primary-light"
            copyrightText={
              (data.copyrightLink?.overwriteTitle ||
                data.copyrightLink?.link?.title) && (
                <span>
                  <LinkWrapper
                    to={data.copyrightLink?.link?.url || null}
                    className="hover:text-text-on-primary-light"
                  >
                    <b>
                      {data.copyrightLink?.overwriteTitle ||
                        data.copyrightLink?.link?.title}
                    </b>
                  </LinkWrapper>
                  {". All rights reserved."}
                </span>
              )
            }
          />
        </div>
      </div>
    </footer>
  );
}

ColumnsWithLeadingCompanyLogo.propTypes = {
  data: PropTypes.object,
  locale: PropTypes.string,
  className: PropTypes.string,
};

export default ColumnsWithLeadingCompanyLogo;
